import axios from 'axios';

export interface PhiNote {
  productName: string;
  type: 'days' | 'petal_drop_80' | 'month_of_year' | string;
  value: string;
}

export interface PhiTrackerRow {
  orchardId: string;
  orchardName: string | null;
  farmName: string | null;
  lastSprayEnd: string;
  maxPhiDays: number;
  earliestSafeHarvest: string;
  phiNotes: PhiNote[];
}

export interface LongRunningSession {
  _id: string;
  programName: string | null;
  startDate: string;
  daysRunning: number;
}

export interface UpcomingSession {
  _id: string;
  programName: string | null;
  machineName: string | null;
  operatorName: string;
  orchardName: string;
  startDate: string;
}

export interface ChemicalStockUsageRow {
  chemicalId:  string;
  productName: string;
  unit:        'L' | 'kg' | 'g' | 'ml' | null;
  draft:       number;
  planned:     number;
  /**
   * Null when the product's on-hand figure is in a unit-conflict state
   * (Slice 4): a cross-family unit conflict means the backend can't safely
   * report a single number. Never do arithmetic on this without a null
   * check; render the `inventory.unitConflict.badge` review pill instead.
   */
  stock:       number | null;
  cPlanned:    number;
  cActual:     number;
  /**
   * Present (with stock: null) when the on-hand figure is unit-conflicted.
   * Derived stockStatus (see chemicalStockUsageMath) also reports 'conflict'
   * whenever stock is null, so this is a reason code only, not a status flag.
   */
  conflictReason?: string;
}

export interface OperatorLicenseRow {
  _id: string;
  name: string;
  licenseNumber: string | null;
  licenseExpiryDate: string | null;
}

export interface MachineCalibrationRow {
  _id: string;
  name: string;
  lastCalibrationDate: string | null;
  nextCalibrationDue: string | null;
}

export interface SpraySummary {
  activePrograms:         { count: number; longRunningSessions: LongRunningSession[] };
  upcomingSessions:       UpcomingSession[];
  chemicalStockUsage:     ChemicalStockUsageRow[];
  operatorLicenses:       OperatorLicenseRow[];
  machineCalibrations:    MachineCalibrationRow[];
  phiTracker:             PhiTrackerRow[];
  settings:               { longRunningSessionDays: number; seasonStartDate: string | null };
  generatedAt:            string;
}

export async function getSpraySummary(): Promise<SpraySummary> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await axios.get<SpraySummary>('/api/v1/spray/dashboard/summary', { headers });
  return res.data;
}
